import moment from 'moment'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { Button, Col, Panel, PanelGroup, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { WithAuth } from '../../Authorization/WithAuthProtocol'
import { CustomCheckbox } from '../../CustomControls/CustomCheckbox'
import { ModalData } from '../../Modals/ModalData'
import { HandleApiError } from '../../Notifications/APIErrorHandler'
import { dateTimeFormatter } from '../../Utils/Formatter'

export const PendingAttachmentRow = ({ attachment, currentUser, protocolId, onToggle, isSaving }) => {
    const { t } = useTranslation();
    const isSelf = attachment.uploadedBy === currentUser;

    return (
        <Row style={{ borderBottom: '1px solid #f0f0f0', padding: '6px 0', alignItems: 'center' }}>
            <Col md={5}><span>{attachment.fileName}</span></Col>
            <Col md={4}>
                <small className="text-muted">
                    {attachment.uploadedByUserName} &middot; {dateTimeFormatter(attachment.uploaded)}
                </small>
            </Col>
            <Col md={3}>
                <WithAuth protocolId={protocolId} requiredPermission={["VisitTracking.Edit"]}>
                    <CustomCheckbox
                        name={`validate-${attachment.fieldId}-${attachment.fieldOrder}`}
                        disabled={isSelf || isSaving}
                        isChecked={!!attachment._checked}
                        label={t("protocols.visit.formTemplate.label_fileUploadField")}
                        onChange={(_name, val) => onToggle(attachment, val)}
                    />
                    {isSelf &&
                        <small className="text-muted">{t("protocolNotifications.pendingAttachments_ownFile")}</small>
                    }
                </WithAuth>
            </Col>
        </Row>
    );
};

PendingAttachmentRow.propTypes = {
    attachment: PropTypes.object.isRequired,
    currentUser: PropTypes.string.isRequired,
    protocolId: PropTypes.number.isRequired,
    onToggle: PropTypes.func.isRequired,
    isSaving: PropTypes.bool,
};

export const PendingAttachmentsModal = ({
    pendingData,
    currentUser,
    protocolId,
    visitId,
    patientId,
    medicalRecordNumber,
    onSaveUpload,
    onContinue,
    onCancel,
}) => {
    const { t } = useTranslation();
    const [forms, setForms] = useState(pendingData.forms.map(f => ({
        ...f,
        pendingAttachments: f.pendingAttachments.map(a => ({ ...a, _checked: false }))
    })));
    const [isSaving, setIsSaving] = useState(false);

    const hasPending = forms.some(f => f.pendingAttachments.some(a => !a._checked));

    const handleToggle = (formItem, attachment, checked) => {
        setForms(prev => prev.map(f =>
            f.patientFormId === formItem.patientFormId
                ? {
                    ...f,
                    pendingAttachments: f.pendingAttachments.map(a =>
                        a.fieldId === attachment.fieldId && a.fieldOrder === attachment.fieldOrder
                            ? { ...a, _checked: checked }
                            : a
                    )
                }
                : f
        ));
    };

    const handleContinue = async () => {
        setIsSaving(true);
        try {
            for (const form of forms) {
                const fieldsToSave = form.pendingAttachments
                    .filter(a => a._checked)
                    .map(a => ({
                        _t: "FileUploadField",
                        id: a.fieldId,
                        order: a.fieldOrder,
                        value: {
                            fileName: a.fileName,
                            validatedBy: currentUser,
                            validated: moment().format(),
                        }
                    }));

                if (fieldsToSave.length > 0) {
                    await onSaveUpload(protocolId, visitId, patientId, medicalRecordNumber, {
                        patientForm: { id: form.patientFormId, fields: fieldsToSave }
                    });
                }
            }
            onContinue();
        } catch (error) {
            HandleApiError(error);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <ModalData
            isShowing={true}
            hide={onCancel}
            title={t("protocolNotifications.pendingAttachments_title")}
            size="large"
            footer={
                <>
                    <Button bsStyle="default" onClick={onCancel} disabled={isSaving}>
                        {t("buttons.cancel")}
                    </Button>
                    <Button bsStyle="success" onClick={handleContinue} disabled={hasPending || isSaving}>
                        {t("buttons.continue")}
                    </Button>
                </>
            }
        >
            <p className="text-muted">{t("protocolNotifications.pendingAttachments_description")}</p>
            <PanelGroup accordion id="accordion-pending-attachments">
                {forms.map(form => {
                    const pendingCount = form.pendingAttachments.filter(a => !a._checked).length;
                    return (
                        <Panel key={form.patientFormId} eventKey={form.patientFormId}>
                            <Panel.Heading>
                                <Panel.Title toggle>
                                    <strong>{form.formTitle}</strong>
                                    {pendingCount > 0 && <span className="text-muted"> ({pendingCount})</span>}
                                </Panel.Title>
                            </Panel.Heading>
                            <Panel.Body collapsible>
                                {form.pendingAttachments.map(att => (
                                    <PendingAttachmentRow
                                        key={`${att.fieldId}-${att.fieldOrder}`}
                                        attachment={att}
                                        currentUser={currentUser}
                                        protocolId={protocolId}
                                        isSaving={isSaving}
                                        onToggle={(a, checked) => handleToggle(form, a, checked)}
                                    />
                                ))}
                            </Panel.Body>
                        </Panel>
                    );
                })}
            </PanelGroup>
        </ModalData>
    );
};

PendingAttachmentsModal.propTypes = {
    pendingData: PropTypes.object.isRequired,
    currentUser: PropTypes.string.isRequired,
    protocolId: PropTypes.number.isRequired,
    visitId: PropTypes.number.isRequired,
    patientId: PropTypes.string.isRequired,
    medicalRecordNumber: PropTypes.string.isRequired,
    onSaveUpload: PropTypes.func.isRequired,
    onContinue: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
};
